/**
 * src/utils/format.js
 *
 * Shared formatting and color-mapping utilities used across all pages.
 * Keep all color/badge logic here so every page stays visually consistent.
 */

// ---------------------------------------------------------------------------
// Duration
// ---------------------------------------------------------------------------

/**
 * Convert a duration in seconds to a human-readable string.
 * Returns "Ongoing" for -1 (session still running) or null/undefined.
 *
 * @param {number|null|undefined} seconds
 * @returns {string}  e.g. "12m 5s", "45s", "2m", "Ongoing"
 */
export function formatDuration(seconds) {
  if (seconds === -1 || seconds === null || seconds === undefined) return 'Ongoing';
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  if (rem === 0) return `${m}m`;
  return `${m}m ${rem}s`;
}

// ---------------------------------------------------------------------------
// Date / time
// ---------------------------------------------------------------------------

/**
 * Format an ISO 8601 string to "Apr 20, 2026 · 14:30".
 * Returns "-" for falsy input.
 *
 * @param {string|null|undefined} isoString
 * @returns {string}
 */
export function formatDateTime(isoString) {
  if (!isoString) return '-';
  try {
    const d = new Date(isoString);
    const date = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day:   'numeric',
      year:  'numeric',
    }).format(d);
    const time = new Intl.DateTimeFormat('en-US', {
      hour:   '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
    return `${date} · ${time}`;
  } catch {
    return isoString;
  }
}

/**
 * Format an ISO 8601 string to "HH:mm:ss" (24-hour clock).
 * Returns "-" for falsy input.
 *
 * @param {string|null|undefined} isoString
 * @returns {string}  e.g. "14:30:45"
 */
export function formatTime(isoString) {
  if (!isoString) return '-';
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour:   '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(new Date(isoString));
  } catch {
    return isoString;
  }
}

// ---------------------------------------------------------------------------
// Status colors (session / schedule)
// ---------------------------------------------------------------------------

/**
 * Return a Tailwind class string for a session/schedule status badge.
 *
 * Covered statuses: ACTIVE, ENDED, CREATED, SCHEDULED, COMPLETED, CANCELLED
 *
 * @param {string} status
 * @returns {string}  combined bg + text + border Tailwind classes
 */
export function statusColor(status) {
  switch ((status || '').toUpperCase()) {
    case 'ACTIVE':
      return 'bg-green-900/50 text-green-400 border border-green-800';
    case 'ENDED':
    case 'COMPLETED':
      return 'bg-slate-800 text-slate-400 border border-slate-700';
    case 'CREATED':
    case 'SCHEDULED':
      return 'bg-blue-900/50 text-blue-400 border border-blue-800';
    case 'CANCELLED':
      return 'bg-red-900/50 text-red-400 border border-red-800';
    default:
      return 'bg-slate-800 text-slate-400 border border-slate-700';
  }
}

// ---------------------------------------------------------------------------
// Severity colors (findings)
// ---------------------------------------------------------------------------

/**
 * Return a Tailwind class string for a finding severity badge.
 *
 * @param {string} severity  CRITICAL | HIGH | MEDIUM | LOW
 * @returns {string}
 */
export function severityColor(severity) {
  switch ((severity || '').toUpperCase()) {
    case 'CRITICAL':
      return 'bg-red-900/60 text-red-400 border border-red-800';
    case 'HIGH':
      return 'bg-orange-900/60 text-orange-400 border border-orange-800';
    case 'MEDIUM':
      return 'bg-yellow-900/60 text-yellow-400 border border-yellow-800';
    case 'LOW':
      return 'bg-emerald-900/60 text-emerald-400 border border-emerald-800';
    default:
      return 'bg-slate-800 text-slate-400 border border-slate-700';
  }
}

// ---------------------------------------------------------------------------
// Risk (overall session risk level)
// ---------------------------------------------------------------------------

/**
 * Return a Tailwind class string for an overall risk level card/badge.
 *
 * @param {string} risk  CRITICAL | HIGH | MEDIUM | LOW | NONE
 * @returns {string}
 */
export function riskColor(risk) {
  switch ((risk || '').toUpperCase()) {
    case 'CRITICAL':
      return 'bg-red-900/40 text-red-300 border border-red-800';
    case 'HIGH':
      return 'bg-orange-900/40 text-orange-300 border border-orange-800';
    case 'MEDIUM':
      return 'bg-yellow-900/40 text-yellow-300 border border-yellow-800';
    case 'LOW':
    case 'NONE':
      return 'bg-emerald-900/40 text-emerald-300 border border-emerald-800';
    default:
      return 'bg-slate-800 text-slate-400 border border-slate-700';
  }
}

// ---------------------------------------------------------------------------
// Bar chart fill colors (severity bars - plain Tailwind bg class only)
// ---------------------------------------------------------------------------

/**
 * Return just the background Tailwind class for severity bar fills.
 *
 * @param {string} severity
 * @returns {string}
 */
export function severityBarColor(severity) {
  switch ((severity || '').toUpperCase()) {
    case 'CRITICAL': return 'bg-red-500';
    case 'HIGH':     return 'bg-orange-500';
    case 'MEDIUM':   return 'bg-yellow-500';
    case 'LOW':      return 'bg-emerald-500';
    default:         return 'bg-slate-600';
  }
}

// ---------------------------------------------------------------------------
// Role colors (org member roles)
// ---------------------------------------------------------------------------

/**
 * Return a Tailwind class string for an organization member role badge.
 *
 * @param {string} role  OWNER | ADMIN | MEMBER
 * @returns {string}
 */
export function roleColor(role) {
  switch ((role || '').toUpperCase()) {
    case 'OWNER':
      return 'bg-teal-50 text-teal-800 border border-teal-300 font-semibold';
    case 'ADMIN':
    case 'ORG_ADMIN':
      return 'bg-teal-50 text-teal-700 border border-teal-200 font-semibold';
    case 'MEMBER':
      return 'bg-slate-100 text-slate-700 border border-slate-200 font-medium';
    default:
      return 'bg-slate-100 text-slate-600 border border-slate-200';
  }
}
